import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { closeModal, fetchBlindages } from "redux/actions/global";
import { reportArticles } from "redux/actions/detail";
import { sendNotice } from "redux/actions/reportedArticles";
import { gqlQuery } from "libs/awsLib";
import { queryArticle } from "gql/index";
import { t, get, getCountryCodeFromSiteId, isDenounceWithImages, stringToPlainText, replace_notPrintableUnicode, getNoticesDayLimit, alertError } from "utils";
import moment from "moment";
import Select from "react-select";
import ReactTable from "react-table";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import Icon from "components/Icon";
import "react-table/react-table.css";

// Validación común para los textArea de denuncia (detalle y campos PPPI8):
// no vacío + mínimo 5 caracteres + caracteres permitidos.
// Devuelve "empty" | "short" | "invalid" o null si es válido.
const validateDetailText = (value) => {
  // Limpia emojis y caracteres no imprimibles antes de validar, igual que se
  // hace al enviar la denuncia, para que no bloqueen un texto legítimo.
  const trimmed = replace_notPrintableUnicode(value || "").trim();
  if (!trimmed) return "empty";
  const string = trimmed.replace(/\s+/g, "");
  if (string.length < 5) return "short";
  const validString = /^[A-Za-z0-9\s.,'&"-_ÁÉÍÓÚÑáéíóúñÄËÏÖÜäëïöüÇçÂÊÎÔÛâêîôûãÃõÕàÀº]{1,5000}$/u.test(string);
  if (validString !== true) return "invalid";
  return null;
};

const MultipleDenounce = (props) => {
  const { articlesIds = [], liveSearch, profile, closeModal, onClose, onSuccess, reportArticles, blindages, history, fetchBlindages, articlesFakeMap, sendNotice, noticeInProgress, sendingNotice, memberRestriction } = props;

  const [selectedInfraction, setSelectedInfraction] = useState(null);
  const [detail, setDetail] = useState("");
  const [desRegNumber, setDesRegNumber] = useState("");
  const [desRegComment, setDesRegComment] = useState("");
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const [errorTextArea, setErrorTextArea] = useState(false);
  const [errorInfraction, setErrorInfraction] = useState(false);
  const [errorDetail, setErrorDetail] = useState(false);
  const [errorDetailEmpty, setErrorDetailEmpty] = useState(false);
  const [border, setBorder] = useState(false);
  const [processing, setProcessing] = useState(false);
  // Estados para rastrear el progreso de envío
  const [sendingDenounces, setSendingDenounces] = useState(false);
  const [totalToSend, setTotalToSend] = useState(0);
  const [successfulCount, setSuccessfulCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [initialNoticeInProgress, setInitialNoticeInProgress] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  // Estado para almacenar los detalles de los errores (articleId -> error reason)
  const [failedErrors, setFailedErrors] = useState({});
  // Estado para indicar si se están cargando los detalles de errores
  const [loadingErrorDetails, setLoadingErrorDetails] = useState(false);
  // Ref para rastrear IDs que fallaron (se actualiza cuando noticeInProgress disminuye pero no están en sendingNotice)
  const failedIdsRef = useRef(new Set());
  // Estado para artículos cargados desde API
  const [loadedArticles, setLoadedArticles] = useState({});
  // Ref para rastrear IDs que ya se están cargando (no causa re-renders)
  const loadingIdsRef = useRef(new Set());

  // Función para obtener artículos seleccionados
  const getSelectedArticles = useCallback(() => {
    if (!articlesIds || articlesIds.length === 0) return [];

    return articlesIds
      .map((id) => {
        // Buscar primero en loadedArticles (artículos cargados desde API)
        let article = loadedArticles[id] || null;

        // Si no está en loadedArticles, buscar en articlesFakeMap (tiene todos los artículos vistos)
        if (!article && articlesFakeMap && articlesFakeMap[id]) {
          article = articlesFakeMap[id];
        }

        // Si no está en articlesFakeMap, buscar en liveSearch.articles (página actual)
        if (!article && liveSearch && liveSearch.articles) {
          article = liveSearch.articles.find((a) => a.id === id);
        }

        if (!article) return null;

        // Obtener siteId del artículo o extraer de su ID
        let siteId = get(article, "site.id", "");
        if (!siteId && article.id) {
          siteId = article.id.trim().slice(0, 3).toUpperCase();
        }

        return {
          id: article.id,
          title: article.title,
          country: get(article, "country.id", ""),
          countryName: get(article, "country.name", ""),
          siteId: siteId,
          catalogListing: get(article, "catalogListing", false),
          catalogProductId: get(article, "catalogProductId", null),
        };
      })
      .filter(Boolean);
  }, [articlesIds, liveSearch, articlesFakeMap, loadedArticles]);

  // Función para cargar artículos faltantes desde la API
  const loadMissingArticles = useCallback(async (missingIds) => {
    if (!missingIds || missingIds.length === 0) return;

    // Filtrar IDs que ya se están cargando
    const idsToLoad = missingIds.filter((id) => !loadingIdsRef.current.has(id));
    if (idsToLoad.length === 0) return;

    // Marcar IDs como cargando
    idsToLoad.forEach((id) => loadingIdsRef.current.add(id));

    try {
      // Cargar artículos en chunks para evitar sobrecarga
      const chunkSize = 25;
      const chunks = [];
      for (let i = 0; i < idsToLoad.length; i += chunkSize) {
        chunks.push(idsToLoad.slice(i, i + chunkSize));
      }

      const loaded = {};
      for (const chunk of chunks) {
        const promises = chunk.map((id) =>
          gqlQuery(queryArticle, { articleId: id, massiveMode: true })
            .then((result) => ({ id, article: result?.article }))
            .catch((error) => {
              console.warn(`Error loading article ${id}:`, error);
              return { id, article: null };
            })
        );

        const results = await Promise.all(promises);
        results.forEach(({ id, article }) => {
          if (article) {
            loaded[id] = article;
          }
        });
      }

      setLoadedArticles((prev) => ({ ...prev, ...loaded }));

      // Remover IDs del set de cargando
      idsToLoad.forEach((id) => loadingIdsRef.current.delete(id));
    } catch (error) {
      console.error("Error loading missing articles:", error);
      // Remover IDs del set de cargando en caso de error
      idsToLoad.forEach((id) => loadingIdsRef.current.delete(id));
    }
  }, []);

  // Actualizar artículos cuando cambian los props o loadedArticles
  useEffect(() => {
    if (!articlesIds || articlesIds.length === 0) {
      setSelectedArticles([]);
      return;
    }

    const articles = articlesIds
      .map((id) => {
        // Buscar primero en loadedArticles (artículos cargados desde API)
        let article = loadedArticles[id] || null;

        // Si no está en loadedArticles, buscar en articlesFakeMap (tiene todos los artículos vistos)
        if (!article && articlesFakeMap && articlesFakeMap[id]) {
          article = articlesFakeMap[id];
        }

        // Si no está en articlesFakeMap, buscar en liveSearch.articles (página actual)
        if (!article && liveSearch && liveSearch.articles) {
          article = liveSearch.articles.find((a) => a.id === id);
        }

        if (!article) return null;

        // Obtener siteId del artículo o extraer de su ID
        let siteId = get(article, "site.id", "");
        if (!siteId && article.id) {
          siteId = article.id.trim().slice(0, 3).toUpperCase();
        }

        return {
          id: article.id,
          title: article.title,
          country: get(article, "country.id", ""),
          countryName: get(article, "country.name", ""),
          siteId: siteId,
          catalogListing: get(article, "catalogListing", false),
          catalogProductId: get(article, "catalogProductId", null),
        };
      })
      .filter(Boolean);

    setSelectedArticles(articles);

    // Identificar artículos faltantes
    const foundIds = new Set(articles.map((a) => a.id));
    const missingIds = articlesIds.filter((id) => !foundIds.has(id));

    // Cargar artículos faltantes solo si hay IDs faltantes
    if (missingIds.length > 0) {
      loadMissingArticles(missingIds);
    }
  }, [articlesIds, liveSearch, articlesFakeMap, loadedArticles, loadMissingArticles]);

  // Obtener blindages cuando cambian los artículos seleccionados
  useEffect(() => {
    const articleIds = selectedArticles.map((a) => a.id);
    if (articleIds.length > 0) {
      fetchBlindages(articleIds);
    }
  }, [selectedArticles, fetchBlindages]);

  // Ref para rastrear si ya se mostró el resumen final
  const summaryShownRef = useRef(false);
  const sendNoticeTimerRef = useRef(null);
  // Ref para rastrear el último estado de noticeInProgress para detectar cambios
  const prevNoticeInProgressRef = useRef([]);

  const isMountedRef = useRef(true);

  // Limpiar el timer de sendNotice al desmontar
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (sendNoticeTimerRef.current) {
        clearTimeout(sendNoticeTimerRef.current);
      }
    };
  }, []);

  // Rastrear el progreso de envío de denuncias
  useEffect(() => {
    if (!sendingDenounces || initialNoticeInProgress.length === 0) return;

    const currentInProgress = noticeInProgress || [];
    const remaining = currentInProgress.length;
    const processed = initialNoticeInProgress.length - remaining;

    // Detectar IDs que se eliminaron de noticeInProgress (se procesaron)
    const prevInProgress = prevNoticeInProgressRef.current || [];
    const removedIds = prevInProgress.filter(id => !currentInProgress.includes(id));

    // sendingNotice contiene los IDs de las denuncias que se enviaron exitosamente
    // Necesitamos comparar con los IDs iniciales (sin el formato catalogProductId)
    const currentSendingNotice = sendingNotice || [];
    const initialIdsWithoutCatalog = initialNoticeInProgress.map(id => {
      // Extraer solo el ID base (antes del guión si hay catalogProductId)
      return id.split("-")[0];
    });

    // Para cada ID removido, verificar si está en sendingNotice
    removedIds.forEach(id => {
      const idBase = id.split("-")[0];
      if (!currentSendingNotice.includes(idBase)) {
        // Este ID se procesó pero no está en sendingNotice, significa que falló
        failedIdsRef.current.add(idBase);
      }
    });

    // Contar cuántas de las iniciales están en sendingNotice (exitosas)
    const successful = initialIdsWithoutCatalog.filter(id =>
      currentSendingNotice.includes(id)
    ).length;

    // Las fallidas son las que están en failedIdsRef o las que se procesaron pero no están en sendingNotice
    const failed = Math.max(failedIdsRef.current.size, processed - successful);

    // Actualizar contadores en tiempo real
    if (!isMountedRef.current) return;
    if (successful !== successfulCount) {
      setSuccessfulCount(successful);
    }
    if (failed !== failedCount && failed >= 0) {
      setFailedCount(failed);
    }

    // Actualizar ref para la próxima comparación
    prevNoticeInProgressRef.current = [...currentInProgress];
  }, [noticeInProgress, sendingNotice, sendingDenounces, initialNoticeInProgress, successfulCount, failedCount, totalToSend]);

  // Mostrar resumen final cuando todas se procesaron
  useEffect(() => {
    if (!sendingDenounces || initialNoticeInProgress.length === 0) {
      return;
    }

    const currentInProgress = noticeInProgress || [];
    const remaining = currentInProgress.length;

    // Cuando todas se procesaron, mostrar resumen
    if (remaining === 0 && initialNoticeInProgress.length > 0 && !summaryShownRef.current) {
      summaryShownRef.current = true;

      // Deseleccionar todos los artículos una vez terminadas las denuncias
      setSelectedArticles([]);

      // Limpiar selección en el componente padre solo después de denunciar
      if (onSuccess) {
        onSuccess();
      }

      // Cerrar el modal después de un pequeño delay para permitir que se complete el proceso
      setTimeout(() => {
        closeModal();
      }, 500);

      // Calcular resultados finales
      const finalSending = sendingNotice || [];
      const initialIdsWithoutCatalog = initialNoticeInProgress.map(id => id.split("-")[0]);
      const finalSuccessful = initialIdsWithoutCatalog.filter(id =>
        finalSending.includes(id)
      ).length;

      // Calcular fallidas: las que no están en sendingNotice + las que están en failedIdsRef
      const failedFromRef = failedIdsRef.current.size;
      const failedFromCalculation = totalToSend - finalSuccessful;
      const finalFailed = Math.max(failedFromRef, failedFromCalculation);

      // Obtener IDs de artículos que fallaron
      const failedIdsArray = initialIdsWithoutCatalog.filter(id =>
        !finalSending.includes(id)
      );

      // Consultar detalles de errores para los artículos que fallaron
      if (failedIdsArray.length > 0) {
        if (isMountedRef.current) setLoadingErrorDetails(true);
        const fetchErrorDetails = async () => {
          const errorDetails = {};
          const promises = failedIdsArray.map(async (articleId) => {
            try {
              const result = await gqlQuery(queryArticle, { articleId, massiveMode: true });
              const reason = result?.article?.noticed?.reason || null;
              if (reason) {
                errorDetails[articleId] = reason;
              } else {
                errorDetails[articleId] = "Sin detalles disponibles";
              }
            } catch (error) {
              console.error(`Error al obtener detalles del artículo ${articleId}:`, error);
              errorDetails[articleId] = "Error al obtener detalles";
            }
          });
          await Promise.all(promises);
          if (isMountedRef.current) {
            setFailedErrors(errorDetails);
            setLoadingErrorDetails(false);
          }
        };
        fetchErrorDetails();
      }

      // Mostrar resumen visualmente en el modal inmediatamente
      if (isMountedRef.current) {
        setShowSummary(true);
        setSuccessfulCount(finalSuccessful);
        setFailedCount(finalFailed);
      }

      // Esperar un poco para que sendingNotice se actualice completamente, luego verificar de nuevo
      const timeoutId = setTimeout(() => {
        if (!isMountedRef.current) return;
        const updatedSending = sendingNotice || [];
        const updatedSuccessful = initialIdsWithoutCatalog.filter(id =>
          updatedSending.includes(id)
        ).length;
        const updatedFailed = Math.max(failedIdsRef.current.size, totalToSend - updatedSuccessful);

        // Actualizar contadores si cambiaron
        if (updatedSuccessful !== finalSuccessful || updatedFailed !== finalFailed) {
          setSuccessfulCount(updatedSuccessful);
          setFailedCount(updatedFailed);
        }
      }, 1500);

      return () => clearTimeout(timeoutId);
    }
  }, [noticeInProgress, sendingNotice, sendingDenounces, totalToSend, initialNoticeInProgress, showSummary, onSuccess, closeModal]);

  const handleReasonsChange = useCallback((selectedOption) => {
    setSelectedInfraction(selectedOption);
    setDetail("");
    setDesRegNumber("");
    setDesRegComment("");
    setBorder(false);
    setShowWarning(false);
    setShowWarningMessage(false);
    setErrorTextArea(false);
    setErrorInfraction(false);
  }, []);

  const handleDetailChange = useCallback((e) => {
    const value = e.target.value;
    setDetail(value);

    // Limpiar errores cuando el usuario empiece a escribir (solo limpiar, no validar)
    setShowWarningMessage(false);
    setErrorTextArea(false);
    setErrorDetailEmpty(false);
    setErrorDetail(false);

    // Solo actualizar el borde visual si hay contenido válido
    const detailTrimmed = value.trim();
    const string = detailTrimmed.replace(/\s+/g, "");
    if (string.length >= 5) {
      setBorder(true);
      setShowWarning(false);
    } else {
      setBorder(false);
      setShowWarning(false);
    }
  }, []);

  const handleRemoveArticle = useCallback((articleId) => {
    setSelectedArticles((prev) => {
      const updated = prev.filter((a) => a.id !== articleId);
      // Si después de quitar el artículo no quedan artículos, cerrar el modal
      // No limpiar selecciones al cerrar, solo cerrar el modal
      if (updated.length === 0 && !processing && !sendingDenounces && !showSummary) {
        closeModal();
        // No llamar onClose aquí porque no queremos limpiar las selecciones
      }
      return updated;
    });
  }, [processing, sendingDenounces, showSummary, closeModal, onClose]);

  const checkBlindageMultiple = useCallback((code) => {
    const blindagesIds = [];
    const blindagesInfoExcel = [];

    if (blindages && Array.isArray(blindages)) {
      blindages.forEach((item) => {
        if (item.reason === code) {
          blindagesIds.push(item.item);
          blindagesInfoExcel.push(item);
        }
      });
    }

    const articleIds = selectedArticles.map((a) => a.id);
    const filteredArticles = articleIds.filter(
      (elemento) => !blindagesIds.includes(elemento)
    );

    return [filteredArticles, blindagesIds, blindagesInfoExcel];
  }, [selectedArticles, blindages]);

  // Función similar a isSameDay de ReportedArticles
  const isSameDay = useCallback(() => {
    const noticesLimitUpdatedAt = profile?.noticesLimitUpdatedAt || moment().valueOf();
    return Boolean(moment(noticesLimitUpdatedAt).isSame(moment(), "day"));
  }, [profile]);

  // Función similar a getUserDenouncesAvailable de ReportedArticles
  const getUserDenouncesAvailable = useCallback(() => {
    if (!memberRestriction) return false;

    let avitableSendDenunces = false;
    let isValidSendDenunces = false;

    if (memberRestriction.status === "INACTIVE" || memberRestriction.status === "SLEEPY") {
      avitableSendDenunces = true;
    } else if (memberRestriction.status === "ACTIVE") {
      if (memberRestriction.denouncesAvailable > 0) {
        avitableSendDenunces = true;
      }
    } else if (memberRestriction.status === "NEW_MEMBER") {
      if (memberRestriction.denouncesAvailable > 0) {
        avitableSendDenunces = true;
      }
    }

    const noticesDayLimit = profile?.noticesLimitDayCount || 0;
    let denouncesSendInSameDay = 0;

    if (isSameDay()) {
      denouncesSendInSameDay = Number(getNoticesDayLimit() - Number(noticesDayLimit + selectedArticles.length));
    } else {
      denouncesSendInSameDay = Number(getNoticesDayLimit() - selectedArticles.length);
    }

    if (denouncesSendInSameDay > 0 && avitableSendDenunces) {
      isValidSendDenunces = true;
    }

    return isValidSendDenunces;
  }, [memberRestriction, profile, isSameDay, selectedArticles.length]);

  // Función similar a selectOldArticles de ReportedArticles
  const selectOldArticles = useCallback(() => {
    const noticesDayLimit = profile?.noticesLimitDayCount || 0;

    // Formatear IDs: "articleId-catalogProductId" o solo "articleId"
    let selectedSortedByDate = selectedArticles.map((article) => {
      const catalogProductId = article.catalogListing && article.catalogProductId
        ? article.catalogProductId
        : null;
      return catalogProductId ? `${article.id}-${catalogProductId}` : article.id;
    });

    // Calcular cuántos puede enviar según el límite diario
    let itemsToDelete = 0;
    if (isSameDay()) {
      itemsToDelete = getNoticesDayLimit() - noticesDayLimit;
    } else {
      itemsToDelete = getNoticesDayLimit();
    }

    // Retornar solo los primeros itemsToDelete artículos (limitado por el límite diario)
    return selectedSortedByDate.splice(0, itemsToDelete);
  }, [selectedArticles, profile, isSameDay]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    // Limpiar todos los errores primero
    setErrorInfraction(false);
    setErrorDetailEmpty(false);
    setErrorDetail(false);
    setErrorTextArea(false);
    setShowWarningMessage(false);

    // Validar artículos seleccionados
    if (selectedArticles.length === 0) {
      return;
    }

    // Validar infracción (pero no hacer return, continuar validando detalle)
    let hasErrors = false;
    if (!selectedInfraction || !selectedInfraction.value) {
      setErrorInfraction(true);
      hasErrors = true;
    }

    const code = selectedInfraction.value;
    const isPPPI8 = code === "PPPI8";

    // Validar campos según tipo de infracción.
    // PPPI8 usa dos textArea (número de registro + descripción); el resto usa
    // un único textArea. En ambos casos aplicamos la misma validación.
    const detailError = isPPPI8
      ? (validateDetailText(desRegNumber) || validateDetailText(desRegComment))
      : validateDetailText(detail);

    setErrorDetailEmpty(detailError === "empty");
    setErrorDetail(detailError === "short");
    setErrorTextArea(detailError === "invalid");
    if (detailError) {
      hasErrors = true;
    }

    // Si hay errores, no continuar
    if (hasErrors) {
      return;
    }

    const codeDescription = t(`report_v2.report_code.${code}`);

    // Verificar disponibilidad de denuncias
    const denouncesAvailable = getUserDenouncesAvailable();
    if (!denouncesAvailable) {
      alertError(t("reported_articles.index.error_send_complaint"));
      return;
    }

    // Seleccionar artículos según el límite diario
    const articlesToSend = selectOldArticles();
    if (articlesToSend.length === 0) {
      alertError(t("reported_articles.index.error_send_complaint"));
      return;
    }

    setProcessing(true);

    // Obtener los IDs base (sin catalogProductId) para reportArticles
    const filteredArticles = articlesToSend.map(id => id.split("-")[0]);

    // Inicializar el estado de progreso
    setTotalToSend(articlesToSend.length);
    setSuccessfulCount(0);
    setFailedCount(0);
    setSendingDenounces(true);
    setInitialNoticeInProgress([...articlesToSend]);
    // Limpiar refs y estados
    summaryShownRef.current = false;
    failedIdsRef.current.clear();
    setFailedErrors({});
    setLoadingErrorDetails(false);
    prevNoticeInProgressRef.current = [...articlesToSend];

    const description = isPPPI8
      ? replace_notPrintableUnicode(stringToPlainText(desRegComment.trim()))
      : replace_notPrintableUnicode(stringToPlainText(detail.trim()));

    const payload = {
      articlesIds: filteredArticles,
      code,
      code_description: codeDescription,
      description,
      design_registration_number: isPPPI8 ? desRegNumber.trim() : null,
      author: {
        id: profile.id,
        givenName: profile.givenName,
        familyName: profile.familyName,
      },
      source: "multiple_denounce_send",
    };

    const reportPayload = {
      ...payload,
      history: history,
    };


    reportArticles(reportPayload);

    sendNoticeTimerRef.current = setTimeout(() => {
      sendNotice({ articlesIds: articlesToSend, source: "multiple_denounce_send" });
    }, 500);
  }, [selectedInfraction, selectedArticles, detail, desRegNumber, desRegComment, checkBlindageMultiple, profile, reportArticles, sendNotice, closeModal, onClose, history, t, getUserDenouncesAvailable, selectOldArticles]);

  const handleMarkAsSuspicious = useCallback((e) => {
    if (e) {
      e.preventDefault();
    }

    // Limpiar todos los errores primero
    setErrorInfraction(false);
    setErrorDetailEmpty(false);
    setErrorDetail(false);
    setErrorTextArea(false);
    setShowWarningMessage(false);

    // Validar artículos seleccionados
    if (selectedArticles.length === 0) {
      return;
    }

    // Validar infracción (pero no hacer return, continuar validando detalle)
    let hasErrors = false;
    if (!selectedInfraction || !selectedInfraction.value) {
      setErrorInfraction(true);
      hasErrors = true;
    }

    const code =selectedInfraction ? selectedInfraction.value : "";
    const isPPPI8 = code === "PPPI8";

    const detailError = isPPPI8
      ? (validateDetailText(desRegNumber) || validateDetailText(desRegComment))
      : validateDetailText(detail);

    setErrorDetailEmpty(detailError === "empty");
    setErrorDetail(detailError === "short");
    setErrorTextArea(detailError === "invalid");
    if (detailError) {
      hasErrors = true;
    }

    // Si hay errores, no continuar
    if (hasErrors) {
      return;
    }

    // Continuar con el procesamiento
    setProcessing(true);

    // Formatear IDs: "articleId-catalogProductId" o solo "articleId"
    const articlesToSend = selectedArticles.map((article) => {
      const catalogProductId = article.catalogListing && article.catalogProductId
        ? article.catalogProductId
        : null;
      return catalogProductId ? `${article.id}-${catalogProductId}` : article.id;
    });

    const filteredArticles = articlesToSend.map(id => id.split("-")[0]);

    const description = isPPPI8
      ? replace_notPrintableUnicode(stringToPlainText(desRegComment.trim()))
      : replace_notPrintableUnicode(stringToPlainText(detail.trim()));

    const payload = {
      articlesIds: filteredArticles,
      code,
      code_description: t(`report_v2.report_code.${code}`),
      description,
      design_registration_number: isPPPI8 ? desRegNumber.trim() : null,
      author: {
        id: profile.id,
        givenName: profile.givenName,
        familyName: profile.familyName,
      },
      source: "multiple_denounce",
    };

    const reportPayload = {
      ...payload,
      history: history,
    };

    reportArticles(reportPayload);

    closeModal();
    if (onSuccess) {
      onSuccess();
    }
  }, [selectedInfraction, detail, desRegNumber, desRegComment, selectedArticles, checkBlindageMultiple, profile, reportArticles, closeModal, history]);

  const handleClose = useCallback(() => {
    setSendingDenounces(false);
    setProcessing(false);
    setShowSummary(false);
    summaryShownRef.current = false;
    failedIdsRef.current.clear();
    setFailedErrors({});
    setLoadingErrorDetails(false);
    closeModal();
    // No limpiar selecciones al cerrar el modal normalmente
    // onClose se llama pero no hace nada (no limpia selecciones)
  }, [closeModal]);


  const infractionOptions = useMemo(() => {
    if (!profile || !profile.allowedNotices || selectedArticles.length === 0) {
      return [];
    }

    const firstArticle = selectedArticles[0];
    let siteId = firstArticle?.siteId || "";

    if (!siteId && firstArticle?.id) {
      siteId = firstArticle.id.trim().slice(0, 3).toUpperCase();
    }

    const siteNotices = profile.allowedNotices.find((site) => site.siteId === siteId);

    if (!siteNotices || !siteNotices.notices || siteNotices.notices.length === 0) {
      return [];
    }

    const notices = siteNotices.notices
      .filter((notice) => !isDenounceWithImages(notice.id)) || [];

    return notices.map((item) => ({
      value: item.id,
      label: t(`multiple_denounce.options.report_code.${item.id}`),
    }));
  }, [profile, selectedArticles, t]);

  // Columnas para ReactTable
  const columns = useMemo(
    () => [
      {
        Header: t("multiple_denounce.table.product"),
        accessor: "title",
        width: 445,
        resizable: false,
        Cell: (row) => (
          <div className="cell-container">
            <div className="cell-content">
              <div className="title-row">{row.value}</div>
            </div>
          </div>
        ),
      },
      {
        Header: t("multiple_denounce.table.country"),
        accessor: "country",
        width: 74,
        resizable: false,
        Cell: (row) => {
          const countryCode = getCountryCodeFromSiteId(row.original.siteId);
          return (
            <div className="cell-container">
              <div className="cell-content">
                <div className="country-row">
                  {/* <img
                    src={`/images/countries/${countryCode}.png`}
                    alt="flag"
                    className="country-flag"
                  /> */}
                  <div className="country-code">{countryCode.toUpperCase()}</div>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        resizable: false,
        Header: "",
        accessor: "id",
        width: 184,
        Cell: (row) => (
          <div className="cell-container">
            <button
              type="button"
              className="btn-link"
              onClick={() => handleRemoveArticle(row.value)}
            >
              {t("multiple_denounce.table.remove_from_selection")}
            </button>
          </div>
        ),
      },
    ],
    [handleRemoveArticle]
  );

  // Calcular progreso actual
  const currentInProgress = noticeInProgress || [];
  const remaining = currentInProgress.length;
  const processed = sendingDenounces && totalToSend > 0 ? totalToSend - remaining : 0;
  const progressPercentage = totalToSend > 0 ? Math.round((processed / totalToSend) * 100) : 0;

  return (
    <div className="modal-multiple-denounce-content">
      <div className="modal-multiple-denounce-header">
        <h4 className="modal-title">{t("multiple_denounce.title")}</h4>
        <p className="instruction-text">
          {t("multiple_denounce.instruction")}
        </p>

        {/* Mensaje de progreso mientras se envían las denuncias */}
        {sendingDenounces && !showSummary && (
          <div className="denounce-progress-container" style={{
            padding: "15px",
            margin: "15px 0",
            backgroundColor: "#f0f8ff",
            borderRadius: "4px",
            border: "1px solid #3498db"
          }}>
            <div style={{ marginBottom: "10px", fontWeight: 600, color: "#2c3e50" }}>
              {t("multiple_denounce.progress.processing")}
            </div>
            <div style={{ marginBottom: "8px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                <span>{t("multiple_denounce.progress.processed")}: {processed} / {totalToSend}</span>
                <span>{progressPercentage}%</span>
              </div>
              <div style={{
                width: "100%",
                height: "20px",
                backgroundColor: "#e0e0e0",
                borderRadius: "10px",
                overflow: "hidden"
              }}>
                <div style={{
                  width: `${progressPercentage}%`,
                  height: "100%",
                  backgroundColor: "#3498db",
                  transition: "width 0.3s ease"
                }}></div>
              </div>
            </div>
            <div style={{ fontSize: "14px", color: "#7f8c8d" }}>
              {remaining > 0
                ? `${t("multiple_denounce.progress.remaining")}: ${remaining}`
                : t("multiple_denounce.progress.finishing")}
            </div>
          </div>
        )}

        {/* Resumen final visual - COMENTADO: Resumen de resultados de denuncias */}
        {false && showSummary && (
          <div className="denounce-summary-container" style={{
            padding: "20px",
            margin: "15px 0",
            backgroundColor: "#e8f5e9",
            borderRadius: "4px",
            border: "2px solid #4caf50"
          }}>
            <div style={{ marginBottom: "15px", fontWeight: 600, fontSize: "18px", color: "#2c3e50" }}>
              {t("multiple_denounce.progress.completed")}
            </div>
            <div style={{ marginBottom: "10px", fontSize: "16px", color: "#424242" }}>
              <div style={{ marginBottom: "8px" }}>
                <strong>{t("multiple_denounce.progress.summary")}:</strong>
              </div>
              <div style={{ marginBottom: "5px", color: "#2e7d32" }}>
                ✓ {t("multiple_denounce.progress.successful")}: <strong>{successfulCount}</strong>
              </div>
              <div style={{ color: failedCount > 0 ? "#c62828" : "#2e7d32", marginBottom: failedCount > 0 ? "10px" : "0" }}>
                {failedCount > 0 ? "✗" : "✓"} {t("multiple_denounce.progress.failed")}: <strong>{failedCount}</strong>
              </div>

              {/* Mostrar detalles de errores si hay fallos */}
              {failedCount > 0 && (
                <div style={{
                  marginTop: "15px",
                  padding: "12px",
                  backgroundColor: "#ffebee",
                  borderRadius: "4px",
                  border: "1px solid #ef5350"
                }}>
                  <div style={{ marginBottom: "8px", fontWeight: 600, color: "#c62828" }}>
                    Detalles de errores:
                  </div>
                  {loadingErrorDetails ? (
                    <div style={{ color: "#616161", fontSize: "14px", fontStyle: "italic" }}>
                      Cargando detalles...
                    </div>
                  ) : Object.keys(failedErrors).length > 0 ? (
                    Object.entries(failedErrors).map(([articleId, reason]) => (
                      <div key={articleId} style={{
                        marginBottom: "8px",
                        padding: "8px",
                        backgroundColor: "#fff",
                        borderRadius: "3px",
                        fontSize: "14px"
                      }}>
                        <div style={{ fontWeight: 600, color: "#424242", marginBottom: "4px" }}>
                          {articleId}:
                        </div>
                        <div style={{ color: "#616161", fontSize: "13px", wordBreak: "break-word" }}>
                          {reason || "Sin detalles disponibles"}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: "#616161", fontSize: "14px" }}>
                      No se pudieron obtener los detalles de los errores.
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setSendingDenounces(false);
                setProcessing(false);
                setShowSummary(false);
                summaryShownRef.current = false;
                failedIdsRef.current.clear();
                setFailedErrors({});
                setLoadingErrorDetails(false);
                closeModal();
                // No limpiar selecciones al cerrar desde el botón OK del resumen
                // Las selecciones ya se limpiaron cuando se completaron las denuncias
              }}
              style={{ marginTop: "10px" }}
            >
              {t("ok")}
            </button>
          </div>
        )}
        {/* Tabla de productos seleccionados */}
        {selectedArticles.length > 0 && (
          <div className="selected-articles-table">
            <ReactTable
              data={selectedArticles}
              columns={columns}
              minRows={0}
              showPagination={false}
              className="multiple-denounce-table"
            />
          </div>
        )}

        <form onSubmit={handleSubmit} className="form-group-multiple-denounce">
          {/* Campo Infracción */}
          <div className="form-group ">
            <label style={{ color: errorInfraction ? "red" : "inherit" }}>
              {t("multiple_denounce.infraction_label")}
            </label>
            <div className="notices-reasons-container input-group">
              {infractionOptions.length === 0 && selectedArticles.length > 0 ? (
                <div className="error-message-field">
                  <Icon icon={faExclamationCircle} className="error-icon" />
                  <span>{t("multiple_denounce.error_loading_infractions")}</span>
                </div>
              ) : (
                <Select
                  placeholder={t("multiple_denounce.infraction_placeholder")}
                  value={selectedInfraction}
                  onChange={handleReasonsChange}
                  options={infractionOptions}
                  isClearable={false}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      width: "100%",
                      borderColor: errorInfraction ? "red" : (infractionOptions.length === 0 && selectedArticles.length > 0 ? "red" : base.borderColor),
                    }),
                    dropdownIndicator: (base, state) => ({
                      ...base,
                      color: "rgba(52, 131, 250, 1)",
                      transition: "transform 0.2s ease",
                      transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
                      "&:hover": {
                        color: "rgba(52, 131, 250, 1)",
                      },
                      "& svg": {
                        display: "inline-block",
                        fill: "currentColor",
                        lineHeight: 1,
                        stroke: "currentColor",
                        strokeWidth: 0,
                        color: "rgba(52, 131, 250, 1)",
                      },
                    }),
                  }}
                />
              )}
              {errorInfraction && (
                <div className="error-message-field">
                  <Icon icon={faExclamationCircle} className="error-icon" />
                  <span>{t("multiple_denounce.error_loading_infractions")}</span>
                </div>
              )}
            </div>
          </div>

          {/* Campo Detalle / PPPI8 */}
          {selectedInfraction?.value === "PPPI8" ? (
            <>
              <div className="form-group">
                <label >{t("report_v2.step_2.pppi8_field1_label")}</label>
                <textarea
                  rows="2"
                  maxLength="100"
                  className={`form-control comment__field${errorDetailEmpty && !desRegNumber.trim() ? " comment__field_error" : ""}`}
                  value={desRegNumber}
                  placeholder={t("report_v2.step_2.pppi8_field1_placeholder")}
                  onChange={e => { setDesRegNumber(e.target.value); setErrorDetailEmpty(false); setErrorDetail(false); setErrorTextArea(false); }}
                  style={{ resize: "none", borderColor: (errorDetailEmpty || errorDetail) ? "rgba(242, 61, 79, 1)" : undefined }}
                />


                <div>
                  {errorDetailEmpty && (
                    <div className="error-message-field">
                      <Icon icon={faExclamationCircle} className="error-icon" />
                      <span>{t("report_v2.step_2.pppi8_error_reg_number")}</span>
                    </div>
                  )}
                  {errorDetail && !errorDetailEmpty && (
                    <div className="error-message-field">
                      <Icon icon={faExclamationCircle} className="error-icon" />
                      <span>{t("add_suspicious_xlsx.comment_warning")}</span>
                    </div>
                  )}
                  {errorTextArea && !errorDetailEmpty && !errorDetail && (
                    <div className="error-message-field">
                      <Icon icon={faExclamationCircle} className="error-icon" />
                      <span>{t("add_suspicious_xlsx.valid_charters")}</span>
                    </div>
                  )}
                </div>
                <div className={`char-count ${showWarning || errorDetail || errorDetailEmpty ? "count-chars-error" :
                  errorTextArea ? "count-chars-error" : ""
                  }`}>
                  <span>{t("report_v2.step_2.pppi8_field1_note")}</span>
                  <span>{desRegNumber.length} / 100</span>
                </div>
              </div>
              <div className="form-group">
                <label >Indica qué aspectos de tu diseño fueron replicados en el producto denunciado, detallando las similitudes específicas</label>
                <textarea
                  rows="3"
                  maxLength="2000"
                  className={`form-control comment__field${errorDetailEmpty && !desRegComment.trim() ? " comment__field_error" : ""}`}
                  value={desRegComment}
                  placeholder="Ej.: Coinciden la forma de la base y la proporción del cuerpo, generando la misma impresión general."
                  onChange={e => { setDesRegComment(e.target.value); setErrorDetailEmpty(false); setErrorDetail(false); setErrorTextArea(false); }}
                  style={{ resize: "none", borderColor: (errorDetailEmpty || errorDetail) ? "rgba(242, 61, 79, 1)" : undefined }}
                />

                <div className={`char-count ${showWarning || errorDetail || errorDetailEmpty ? "count-chars-error" :
                  errorTextArea ? "count-chars-error" : ""
                  }`}>
                  <div>
                    {errorDetailEmpty && (
                      <div className="error-message-field">
                        <Icon icon={faExclamationCircle} className="error-icon" />
                        <span>{t("multiple_denounce.error_detail_empty")}</span>
                      </div>
                    )}
                    {errorDetail && !errorDetailEmpty && (
                      <div className="error-message-field">
                        <Icon icon={faExclamationCircle} className="error-icon" />
                        <span>{t("add_suspicious_xlsx.comment_warning")}</span>
                      </div>
                    )}
                    {errorTextArea && !errorDetailEmpty && !errorDetail && (
                      <div className="error-message-field">
                        <Icon icon={faExclamationCircle} className="error-icon" />
                        <span>{t("add_suspicious_xlsx.valid_charters")}</span>
                      </div>
                    )}
                  </div>
                  <span>{desRegComment.length} / 2000</span>
                </div>
              </div>
            </>
          ) : (
            <div className="form-group">
              <label style={{ color: (errorDetailEmpty || errorDetail) ? "rgba(242, 61, 79, 1)" : "inherit", fontSize: (errorDetailEmpty || errorDetail) ? "13px" : "inherit", fontWeight: (errorDetailEmpty || errorDetail) ? "600" : "inherit" }}>
                {t("multiple_denounce.detail_label")}
              </label>
              <textarea
                className={`form-control ${showWarning || errorDetail || errorDetailEmpty ? "comment__field_error" :
                  errorTextArea ? "comment__field_error" :
                    border ? "comment__field_success" : ""
                  }`}
                rows="4"
                value={detail}
                onChange={handleDetailChange}
                placeholder={t("multiple_denounce.detail_placeholder")}
                maxLength={2000}
                style={{ resize: "none", borderColor: (errorDetailEmpty || errorDetail) ? "rgba(242, 61, 79, 1)" : undefined }}
              />
              <div className={`char-count ${showWarning || errorDetail || errorDetailEmpty ? "count-chars-error" :
                errorTextArea ? "count-chars-error" : ""
                }`}>
                <div>
                  {errorDetailEmpty && (
                    <div className="error-message-field">
                      <Icon icon={faExclamationCircle} className="error-icon" />
                      <span>{t("multiple_denounce.error_detail_empty")}</span>
                    </div>
                  )}
                  {errorDetail && !errorDetailEmpty && (
                    <div className="error-message-field">
                      <Icon icon={faExclamationCircle} className="error-icon" />
                      <span>{t("add_suspicious_xlsx.comment_warning")}</span>
                    </div>
                  )}
                </div>
                <span>{detail.length} / 2000</span>
              </div>
              {errorTextArea && (
                <div className="error-message-field">
                  <Icon icon={faExclamationCircle} className="error-icon" />
                  <span>{t("add_suspicious_xlsx.valid_charters")}</span>
                </div>
              )}
            </div>
          )}

          {/* Botones */}
          <div className="modal-footer">
            <div className="footer-buttons-container">
              <div className="footer-buttons-left">
                <button
                  type="submit"
                  className="btn"
                >
                  {t("multiple_denounce.send_denounce")}
                </button>

                <button
                  type="button"
                  className="btn-suspicious btn"
                  onClick={handleMarkAsSuspicious}
                >
                  {t("multiple_denounce.mark_as_suspicious")}
                </button>
              </div>
              <div className="footer-buttons-right">
                <button type="button" className="btn-link" onClick={handleClose}>
                  {t("cancel")}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div >
  );
};

const mapStateToProps = (state) => ({
  profile: state.global.profile,
  liveSearch: state.detail.liveSearch,
  blindages: state.global.blindages,
  articlesFakeMap: state.detail.articlesFakeMap || {},
  noticeInProgress: state.reportedArticles?.noticeInProgress || [],
  sendingNotice: state.reportedArticles?.sendingNotice || [],
  memberRestriction: state.global.memberRestriction,
});

export default withRouter(
  connect(mapStateToProps, {
    closeModal,
    reportArticles,
    fetchBlindages,
    sendNotice
  })(MultipleDenounce)
);
